import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { GET } from "../../services/axios.service";
import { RootState } from "../../app/store";
import { partyText } from "../../static/materialLine";
import { PartyOptionProps } from "../../types/materialLine";
import {
  TurningOrderDateBy,
  TurningOrderListProps,
  TurningOrderProps,
  TurningOrderSortBy,
  TurningOrderSortOrder,
  TurningOrderState,
  TurningPartyInfoProps,
} from "../../types/turningOrder";

// Turning orders, like the cutting orders slice without the material: no material list, and the party info carries the
// cut pieces ready instead of the party's material.
export interface TurningOrdersState {
  orders: TurningOrderListProps[];
  totalOrders: number;
  currentOrder: TurningOrderProps | null;
  currentOrderLoading: boolean;
  partyOptions: PartyOptionProps[];
  filterPartyOptions: PartyOptionProps[];
  partyInfo: TurningPartyInfoProps;
  partyInfoLoading: boolean;
  partyInfoRequestId: string | null;
  loading: boolean;
  error: string | null;
}

const emptyPartyInfo: TurningPartyInfoProps = {
  inhouse: false,
  options: [],
  ready: {},
};

const initialState: TurningOrdersState = {
  orders: [],
  totalOrders: 0,
  currentOrder: null,
  currentOrderLoading: false,
  partyOptions: [],
  filterPartyOptions: [],
  partyInfo: emptyPartyInfo,
  partyInfoLoading: false,
  partyInfoRequestId: null,
  loading: false,
  error: null,
};

const defaultError = "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.";

type FetchTurningOrderListDataProps = {
  page: number;
  size?: number;
  search?: string | null;
  fromDate?: string | null;
  toDate?: string | null;
  party_id?: number | null;
  status?: TurningOrderState | null;
  date_by?: TurningOrderDateBy;
  sort_by?: TurningOrderSortBy;
  sort_order?: TurningOrderSortOrder;
};

export const fetchTurningOrderListData = createAsyncThunk(
  "fetchTurningOrderListData",
  async (req: FetchTurningOrderListDataProps) => {
    let response: any;
    await GET(`/v1/admin/turning-orders`, { ...req })
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

export const fetchTurningOrderDetail = createAsyncThunk(
  "fetchTurningOrderDetail",
  async (id: number) => {
    let response: any;
    await GET(`/v1/admin/turning-orders/${id}`, {})
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

// Loads the open order again without clearing it first, so the page keeps showing it (no loader), e.g. after one of its
// receives was deleted.
export const refreshTurningOrderDetail = createAsyncThunk(
  "refreshTurningOrderDetail",
  async (id: number) => {
    let response: any;
    await GET(`/v1/admin/turning-orders/${id}`, {})
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

// partyId keeps an edited order's own party in the list.
export const fetchTurningOrderParties = createAsyncThunk(
  "fetchTurningOrderParties",
  async (partyId?: number | null) => {
    let response: any;
    await GET(`/v1/admin/turning-orders/parties`, {
      party_id: partyId || null,
    })
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

// The list filter's parties: every turning party, active or not, and every party that has orders.
export const fetchTurningOrderFilterParties = createAsyncThunk(
  "fetchTurningOrderFilterParties",
  async () => {
    let response: any;
    await GET(`/v1/admin/turning-orders/parties`, { all: 1 })
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

// The items the party can be asked to turn and the cut pieces ready; order_id counts the edited order's own lines as
// ready for it.
export const fetchTurningOrderPartyInfo = createAsyncThunk(
  "fetchTurningOrderPartyInfo",
  async (req: { party_id: number; order_id?: number | null }) => {
    let response: any;
    await GET(`/v1/admin/turning-orders/party-info`, { ...req })
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

export const turningOrdersSlice = createSlice({
  name: "turningOrders",
  initialState,
  reducers: {
    clearCurrentTurningOrder: (state) => {
      state.currentOrder = null;
      state.currentOrderLoading = false;
    },
    clearTurningOrderPartyInfo: (state) => {
      state.partyInfo = emptyPartyInfo;
      state.partyInfoLoading = false;
      state.partyInfoRequestId = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchTurningOrderListData.pending, (state) => {
      state.orders = [];
      state.loading = true;
      state.error = null;
    });
    builder.addCase(
      fetchTurningOrderListData.fulfilled,
      (state, action: any) => {
        state.loading = false;
        if (action.payload.data.status === "success") {
          state.orders = action.payload.data.data.records;
          state.totalOrders = action.payload.data.data.total_records;
          state.error = null;
        } else {
          state.orders = [];
          state.error = action?.payload?.data?.message || defaultError;
        }
      }
    );
    builder.addCase(
      fetchTurningOrderListData.rejected,
      (state, action: any) => {
        state.orders = [];
        state.loading = false;
        state.error = action?.payload?.data?.message || defaultError;
      }
    );

    builder.addCase(fetchTurningOrderDetail.pending, (state) => {
      state.currentOrderLoading = true;
      state.currentOrder = null;
    });
    builder.addCase(fetchTurningOrderDetail.fulfilled, (state, action: any) => {
      state.currentOrderLoading = false;
      if (action.payload.data.status === "success") {
        state.currentOrder = action.payload.data.data;
      }
    });
    builder.addCase(fetchTurningOrderDetail.rejected, (state) => {
      state.currentOrderLoading = false;
      state.currentOrder = null;
    });

    builder.addCase(refreshTurningOrderDetail.fulfilled, (state, action: any) => {
      if (action.payload?.data?.status === "success") {
        state.currentOrder = action.payload.data.data;
      }
    });

    builder.addCase(fetchTurningOrderParties.fulfilled, (state, action: any) => {
      if (action.payload.data.status === "success") {
        state.partyOptions = action.payload.data.data.records.map(
          (party: any) => ({ value: party.id, label: partyText(party) })
        );
      }
    });

    builder.addCase(
      fetchTurningOrderFilterParties.fulfilled,
      (state, action: any) => {
        if (action.payload.data.status === "success") {
          state.filterPartyOptions = action.payload.data.data.records.map(
            (party: any) => ({ value: party.id, label: partyText(party) })
          );
        }
      }
    );

    // Only the latest request counts, so a slow answer for a party picked earlier can't overwrite it.
    builder.addCase(fetchTurningOrderPartyInfo.pending, (state, action) => {
      state.partyInfo = emptyPartyInfo;
      state.partyInfoLoading = true;
      state.partyInfoRequestId = action.meta.requestId;
    });
    builder.addCase(
      fetchTurningOrderPartyInfo.fulfilled,
      (state, action: any) => {
        if (action.meta.requestId !== state.partyInfoRequestId) return;
        state.partyInfoLoading = false;
        state.partyInfo =
          action.payload.data.status === "success"
            ? { ...emptyPartyInfo, ...action.payload.data.data }
            : emptyPartyInfo;
      }
    );
    builder.addCase(fetchTurningOrderPartyInfo.rejected, (state, action) => {
      if (action.meta.requestId !== state.partyInfoRequestId) return;
      state.partyInfoLoading = false;
    });
  },
});

export const { clearCurrentTurningOrder, clearTurningOrderPartyInfo } =
  turningOrdersSlice.actions;

export const getTurningOrders = (state: RootState) =>
  state.turningOrders.orders;
export const getTotalTurningOrders = (state: RootState) =>
  state.turningOrders.totalOrders;
export const getCurrentTurningOrder = (state: RootState) =>
  state.turningOrders.currentOrder;
export const getCurrentTurningOrderLoading = (state: RootState) =>
  state.turningOrders.currentOrderLoading;
export const getTurningOrderPartyOptions = (state: RootState) =>
  state.turningOrders.partyOptions;
export const getTurningOrderFilterPartyOptions = (state: RootState) =>
  state.turningOrders.filterPartyOptions;
export const getTurningOrderPartyInfo = (state: RootState) =>
  state.turningOrders.partyInfo;
export const getTurningOrderPartyInfoLoading = (state: RootState) =>
  state.turningOrders.partyInfoLoading;

export default turningOrdersSlice.reducer;
